import { useEffect, useState } from 'react'
import { useParams, useNavigate } from 'react-router-dom'
import {
  Accordion,
  ActionIcon,
  Badge,
  Box,
  Button,
  Checkbox,
  Container,
  Group,
  Loader,
  Stack,
  Text,
  ThemeIcon,
  Timeline,
  Title,
  Tooltip,
} from '@mantine/core'
import { notifications } from '@mantine/notifications'
import {
  ArrowLeft,
  CalendarDays,
  ChevronDown,
  Plus,
  Send,
  SendHorizontal,
  Trash2,
} from 'lucide-react'
import {
  plansApi,
  type PlanSessionDetail,
  type PlanActivityItem,
  type StudentPlanMeta,
} from '../../api/plans'
import { useAuth } from '../../context/AuthContext'
import { TaskMgr } from './TaskMgr'

// Color map for activity types
const ACTIVITY_COLORS: Record<string, string> = {
  learning: 'blue',
  test: 'orange',
  review: 'teal',
  enhance: 'violet',
  video: 'pink',
  meeting: 'gray',
  vocabulary: 'green',
}

function activityColor(type: string) {
  return ACTIVITY_COLORS[type] ?? 'gray'
}

function ActivityList({ activities }: { activities: PlanActivityItem[] }) {
  if (activities.length === 0) {
    return <Text size="sm" c="dimmed">No activities in this session.</Text>
  }
  return (
    <Stack gap={6}>
      {activities.map((act) => (
        <Group key={act.id} gap="sm" align="flex-start">
          <Badge
            color={activityColor(act.activity_type)}
            variant="light"
            size="sm"
            miw={80}
            style={{ flexShrink: 0 }}
          >
            {act.activity_type}
          </Badge>
          <Box>
            <Text size="sm" fw={500}>{act.title}</Text>
            {act.description && (
              <Text size="xs" c="dimmed">{act.description}</Text>
            )}
          </Box>
        </Group>
      ))}
    </Stack>
  )
}

export function PlanMgr() {
  const { planId } = useParams<{ planId: string }>()
  const navigate = useNavigate()
  const { user } = useAuth()
  const [sessions, setSessions] = useState<PlanSessionDetail[]>([])
  const [planMeta, setPlanMeta] = useState<StudentPlanMeta | null>(null)
  const [loading, setLoading] = useState(true)
  // Track which sessions have their accordion open
  const [openSessions, setOpenSessions] = useState<Set<number>>(new Set())
  // Track which sessions are being assigned
  const [assigning, setAssigning] = useState<Set<number>>(new Set())
  const [assigningAll, setAssigningAll] = useState(false)
  const [showUnassignedOnly, setShowUnassignedOnly] = useState(false)

  // Insert-type picker state
  const [pickerOpen, setPickerOpen] = useState(false)
  const [insertPosition, setInsertPosition] = useState<number | null>(null)

  function reloadSessions() {
    if (!planId) return
    plansApi
      .getPlanSessions(Number(planId))
      .then((res) => setSessions(res.data))
      .catch(() =>
        notifications.show({ title: 'Error', message: 'Failed to reload plan sessions', color: 'red' }),
      )
  }

  useEffect(() => {
    if (!planId) return
    setLoading(true)
    Promise.all([
      plansApi.getPlanSessions(Number(planId)),
      plansApi.getPlanMeta(Number(planId)),
    ])
      .then(([sessionsRes, metaRes]) => {
        setSessions(sessionsRes.data)
        setPlanMeta(metaRes.data)
      })
      .catch(() => notifications.show({ title: 'Error', message: 'Failed to load plan', color: 'red' }))
      .finally(() => setLoading(false))
  }, [planId])

  function toggleDetail(sessionId: number) {
    setOpenSessions((prev) => {
      const next = new Set(prev)
      if (next.has(sessionId)) {
        next.delete(sessionId)
      } else {
        next.add(sessionId)
      }
      return next
    })
  }

  function handleAssignAll() {
    if (!planId) return
    setAssigningAll(true)
    plansApi
      .assignAllSessions(Number(planId))
      .then((res) => {
        notifications.show({ title: 'Assigned', message: res.data.message, color: 'green' })
        setSessions((prev) => prev.map((s) => ({ ...s, status: 'assigned' })))
      })
      .catch(() =>
        notifications.show({ title: 'Error', message: 'Failed to assign all sessions', color: 'red' })
      )
      .finally(() => setAssigningAll(false))
  }

  function handleInsertAt(position: number) {
    if (!planId) return
    setInsertPosition(position)
    setPickerOpen(true)
  }

  function handleAssign(session: PlanSessionDetail) {
    setAssigning((prev) => new Set(prev).add(session.id))
    plansApi
      .assignSession(session.id)
      .then((res) => {
        notifications.show({ title: 'Assigned', message: res.data.message, color: 'green' })
        // Optimistically mark session as assigned in local state
        setSessions((prev) =>
          prev.map((s) => (s.id === session.id ? { ...s, status: 'assigned' } : s))
        )
      })
      .catch(() =>
        notifications.show({ title: 'Error', message: 'Failed to create assignments', color: 'red' })
      )
      .finally(() =>
        setAssigning((prev) => {
          const next = new Set(prev)
          next.delete(session.id)
          return next
        })
      )
  }

  return (
    <Container size="md" py="xl">
      <style>{`
        .sat-tl-slot { position: relative; }
        .sat-tl-slot.sat-tl-slot--first { padding-top: 22px; }
        .sat-tl-slot .sat-tl-insert {
          position: absolute;
          left: -20px;
          bottom: -16px;
          transform: translateX(-50%);
          opacity: 0;
          transition: opacity 120ms ease-in-out;
          z-index: 2;
        }
        .sat-tl-slot .sat-tl-insert--before-first {
          position: absolute;
          left: -20px;
          top: 2px;
          transform: translateX(-50%);
          opacity: 0;
          transition: opacity 120ms ease-in-out;
          z-index: 2;
        }
        .sat-tl-slot:hover .sat-tl-insert,
        .sat-tl-slot:hover .sat-tl-insert--before-first { opacity: 1; }
        .sat-type-card:hover {
          transform: translateY(-2px);
          border-color: var(--mantine-color-blue-4);
        }
      `}</style>
      <Stack gap="lg">
        <Group gap="sm">
          <Button
            variant="subtle"
            leftSection={<ArrowLeft size={16} />}
            onClick={() => navigate('/tutor/plans')}
            radius="xl"
            size="sm"
          >
            Back to Plans
          </Button>
        </Group>

        <Group justify="space-between" align="flex-end">
          <Box>
            <Title order={2} c="var(--sat-text)">Plan Sessions</Title>
            <Text c="var(--sat-text-muted)" mt={4}>
              {sessions.length} session{sessions.length !== 1 ? 's' : ''}
            </Text>
          </Box>
          {sessions.length > 0 && (
            <Group gap="md" align="center">
              <Checkbox
                label="Show Unassigned Session Only"
                checked={showUnassignedOnly}
                onChange={(e) => setShowUnassignedOnly(e.currentTarget.checked)}
              />
              <Button
                variant="filled"
                color="teal"
                radius="xl"
                leftSection={<SendHorizontal size={16} />}
                loading={assigningAll}
                onClick={handleAssignAll}
              >
                Assign All
              </Button>
            </Group>
          )}
        </Group>

        {loading ? (
          <Group justify="center" py="xl">
            <Loader />
          </Group>
        ) : sessions.length === 0 ? (
          <Text c="var(--sat-text-muted)" ta="center" py="xl">
            No sessions found for this plan.
          </Text>
        ) : (
          (() => {
            const visibleSessions = sessions
              .map((session, idx) => ({ session, idx }))
              .filter(({ session }) => !showUnassignedOnly || session.status !== 'assigned')
            if (visibleSessions.length === 0) {
              return (
                <Text c="var(--sat-text-muted)" ta="center" py="xl">
                  No unassigned sessions.
                </Text>
              )
            }
            return (
          <Timeline active={-1} bulletSize={36} lineWidth={2} color="blue">
            {visibleSessions.map(({ session, idx }, visIdx, arr) => (
              <Timeline.Item
                key={session.id}
                bullet={
                  <ThemeIcon size={36} radius="xl" color="blue" variant="light">
                    <CalendarDays size={16} />
                  </ThemeIcon>
                }
              >
                <Stack
                  gap="xs"
                  mt={4}
                  className={`sat-tl-slot${visIdx === 0 ? ' sat-tl-slot--first' : ''}`}
                >
                  {visIdx === 0 && (
                    <Tooltip label={`Insert a new session before Day ${idx + 1}`} withArrow>
                      <ActionIcon
                        className="sat-tl-insert--before-first"
                        size={24}
                        radius="xl"
                        variant="filled"
                        color="blue"
                        aria-label="Insert session before this day"
                        onClick={() => handleInsertAt(session.display_order)}
                      >
                        <Plus size={14} />
                      </ActionIcon>
                    </Tooltip>
                  )}
                  {visIdx < arr.length - 1 && (
                    <Tooltip label={`Insert a new session after Day ${idx + 1}`} withArrow>
                      <ActionIcon
                        className="sat-tl-insert"
                        size={24}
                        radius="xl"
                        variant="filled"
                        color="blue"
                        aria-label="Insert session here"
                        onClick={() => handleInsertAt(session.display_order + 1)}
                      >
                        <Plus size={14} />
                      </ActionIcon>
                    </Tooltip>
                  )}
                  <Group gap="xs" align="center" wrap="wrap">
                    <Text fw={700} size="md" c="var(--sat-text)">
                      Day {idx + 1} — {session.title}
                    </Text>
                    {session.status === 'assigned' && (
                      <Tooltip label="Assignments created for this session" withArrow>
                        <Badge color="blue" variant="filled" size="sm" radius="xl">
                          Assigned
                        </Badge>
                      </Tooltip>
                    )}
                  </Group>
                  {session.description && (
                    <Text size="sm" c="var(--sat-text-muted)">
                      {session.description}
                    </Text>
                  )}

                  <Group gap="sm" mt={4}>
                    <Button
                      size="xs"
                      variant="light"
                      color="blue"
                      radius="xl"
                      leftSection={<ChevronDown size={14} />}
                      onClick={() => toggleDetail(session.id)}
                    >
                      {openSessions.has(session.id) ? 'Hide Detail' : 'Show Detail'}
                    </Button>
                    <Button
                      size="xs"
                      variant="filled"
                      color="teal"
                      radius="xl"
                      leftSection={<Send size={14} />}
                      loading={assigning.has(session.id)}
                      onClick={() => handleAssign(session)}
                    >
                      Assign
                    </Button>
                    <Button
                      size="xs"
                      variant="light"
                      color="red"
                      radius="xl"
                      leftSection={<Trash2 size={14} />}
                      onClick={() => {
                        // TODO: implement remove session activity
                        console.log('Remove session activity:', session.id)
                      }}
                    >
                      Remove
                    </Button>
                  </Group>

                  {openSessions.has(session.id) && (
                    <Accordion
                      variant="separated"
                      radius="md"
                      mt="xs"
                      defaultValue="activities"
                    >
                      <Accordion.Item value="activities">
                        <Accordion.Control>
                          <Text size="sm" fw={600}>
                            Activities ({session.activities.length})
                          </Text>
                        </Accordion.Control>
                        <Accordion.Panel>
                          <ActivityList activities={session.activities} />
                        </Accordion.Panel>
                      </Accordion.Item>
                    </Accordion>
                  )}
                </Stack>
              </Timeline.Item>
            ))}
          </Timeline>
            )
          })()
        )}
      </Stack>

      {planId && (
        <TaskMgr
          opened={pickerOpen}
          onClose={() => setPickerOpen(false)}
          plan_id={Number(planId)}
          insert_position={insertPosition}
          target_student_id={planMeta?.student_id ?? null}
          current_user_id={user?.id ?? 0}
          role={user?.role ?? ''}
          onPlanSessionsUpdated={reloadSessions}
        />
      )}
    </Container>
  )
}
